import { PopupAlertModal } from "components/common/modals/PopupAlertModal";
import { hasStylesToRemoveInLists } from "components/tlfpage/remove-style-functions/hasStylesToRemoveInLists";
import { hasStylesToRemoveInSingles } from "components/tlfpage/remove-style-functions/hasStylesToRemoveInSingles";
import { StyleRemovalType } from "components/tlfpage/remove-style-functions/interface";
import { RemoveStylesModal } from "components/tlfpage/RemoveStylesModal";
import { CopyReportModal } from "components/toc/CopyReportModal";
import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { SAVE_HIERARCHY, SET_HIERARCHY_ID } from "contexts/hierarchy/interface";
import { tocVersionString } from "contexts/toc/tocVersionString";
import { eAccessRights, eApplicationPermission, eHierarchyLevel } from "functions/api/enums";
import { downloadAnnotationsCSV } from "functions/api/hierarchy/downloadAnnotationCSV";
import { downloadArsReport } from "functions/api/hierarchy/downloadArsReport";
import { useDocumentTitle } from "functions/useDocumentTitle";
import { useEffect, useState } from "react";
import { NavDropdown } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import { TopBar } from "../topbar/TopBar";
import { LoadXmlLibraryModal } from "./LoadXmlLibraryModal";

/**
 * Top bar for the hierarchy page. Shows the current hierarchy title, ToC
 * version when applicable, and file/menu actions such as load, save, upload
 * and report operations.
 */
export const HierarchyTopBar = (): React.ReactElement => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const location = useLocation();
  const urlParams = useParams();
  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);
  const [showLoadModal, setShowLoadModal] = useState<boolean>(false);
  const [showRemoveStylesModal, setShowRemoveStylesModal] = useState<StyleRemovalType | null>(null);
  const [arsErrorText, setArsErrorText] = useState<string | null>(null);

  useEffect(() => {
    if (
      hierarchyContext &&
      urlParams.hierarchyId !== undefined &&
      hierarchyContext.id !== urlParams.hierarchyId
    ) {
      hierarchyContext.dispatch({
        operation: SET_HIERARCHY_ID,
        hierarchyId: urlParams.hierarchyId,
      });
    }
  }, [hierarchyContext, urlParams]);

  useDocumentTitle(hierarchyContext?.title ?? "Loading hierarchy...");

  const tocVersion = ` (${tocVersionString(
    hierarchyContext?.hierarchy?.tocMajorVersion,
    hierarchyContext?.hierarchy?.tocMinorVersion,
    hierarchyContext?.hierarchy?.isBackupVersion,
  )})`;

  // Determine if we should show Remove Styles option based on current route
  const shouldShowRemoveStyles =
    (hierarchyContext?.hierarchy?.level === eHierarchyLevel.Study ||
      hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report) &&
    (location?.pathname?.includes("/Lists") || location?.pathname?.includes("/Replacements")) &&
    hierarchyContext?.accessRights?.includes(eAccessRights.WriteToTlfItemsInHierarchy);
  const replacementLevelFromPath = location?.pathname?.includes("/Lists")
    ? "HierarchyLists"
    : "HierarchySingles";

  /**
   * Check if there are styles to remove in the current hierarchy context.
   * Calculated inline to determine if the menu option should be disabled.
   */
  const hasStylesToRemove =
    hierarchyContext?.hierarchy &&
    (replacementLevelFromPath === "HierarchyLists"
      ? hasStylesToRemoveInLists(hierarchyContext.hierarchy.lists)
      : hasStylesToRemoveInSingles(hierarchyContext.hierarchy.singles));

  return (
    <>
      <TopBar
        title={`${!hierarchyContext?.title ? "MOSAIC Biometrics" : hierarchyContext.title}${
          hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report ? tocVersion : ""
        }${hierarchyContext?.changed ? "*" : ""}`}
        fileEntry={
          <>
            <NavDropdown.Item
              id="topbar-load-link"
              as={Link}
              to={`${
                hierarchyContext && hierarchyContext.hierarchy
                  ? getHierarchyBaseUrl(hierarchyContext.hierarchy)
                  : "Corporate"
              }/Load`}
            >
              Load TLF
            </NavDropdown.Item>
            <NavDropdown.Divider />
            <NavDropdown.Item
              id="hierarchypage-topbar-save-link"
              disabled={!hierarchyContext?.accessRights?.includes(eAccessRights.MaintainHierarchy)}
              onClick={() => {
                hierarchyContext?.dispatch({ operation: SAVE_HIERARCHY });
              }}
            >
              Save
            </NavDropdown.Item>
            {(hierarchyContext?.hierarchy?.level === eHierarchyLevel.Study ||
              hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report) && (
              <NavDropdown.Item
                id="hierarchypage-topbar-download-ars-json"
                disabled={hierarchyContext.changed}
                onClick={async () => {
                  const response = await downloadArsReport(
                    appSettings.backEndUrl,
                    hierarchyContext.id,
                    hierarchyContext.hierarchy!.name,
                  );
                  if (!response.success) {
                    setArsErrorText(response.errorText);
                  }
                }}
              >
                Export ARS document
              </NavDropdown.Item>
            )}

            <NavDropdown.Divider />
            <NavDropdown.Item
              id="hierarchypage-topbar-remove-styles"
              onClick={() => {
                setShowRemoveStylesModal(replacementLevelFromPath);
              }}
              disabled={!shouldShowRemoveStyles || !hasStylesToRemove}
            >
              Remove styles
            </NavDropdown.Item>
            {(hierarchyContext?.accessRights?.includes(eAccessRights.CreateHierarchy) ||
              appSettings.applicationPermissions.includes(
                eApplicationPermission.AllowDefineXmlUpload,
              )) &&
              (hierarchyContext?.hierarchy?.level === eHierarchyLevel.Corporate ||
                hierarchyContext?.hierarchy?.level === eHierarchyLevel.TA ||
                hierarchyContext?.hierarchy?.level === eHierarchyLevel.Study) && (
                <>
                  <NavDropdown.Item
                    onClick={() => {
                      setShowLoadModal(true);
                    }}
                  >
                    Upload XML
                  </NavDropdown.Item>
                  <LoadXmlLibraryModal
                    onClose={() => {
                      setShowLoadModal(false);
                    }}
                    show={showLoadModal}
                  />
                </>
              )}
            {hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report && (
              <>
                <NavDropdown.Item
                  id="reportpage-topbar-download-annotations-csv"
                  disabled={hierarchyContext.changed}
                  onClick={() => {
                    const dNumber =
                      hierarchyContext?.hierarchyNodes.find(
                        (h) => h.id === hierarchyContext.hierarchy?.parentId,
                      )?.dNumber ??
                      // istanbul ignore next
                      "Report";
                    downloadAnnotationsCSV(
                      `${appSettings?.backEndUrl}Reports/${hierarchyContext.id}/AnnotatedVariables`,
                      `Annotations_${dNumber}_${hierarchyContext.hierarchy!.name}.csv`,
                    );
                  }}
                >
                  Download annotations CSV
                </NavDropdown.Item>
                <NavDropdown.Item
                  id="reportpage-topbar-copy-link"
                  disabled={hierarchyContext.changed}
                  onClick={() => {
                    setShowCopyModal(true);
                  }}
                >
                  Copy report
                </NavDropdown.Item>
                <CopyReportModal
                  onClose={() => {
                    setShowCopyModal(false);
                  }}
                  show={showCopyModal}
                />
              </>
            )}
          </>
        }
      />
      {showRemoveStylesModal && (
        <RemoveStylesModal
          styleRemovalLevel={showRemoveStylesModal}
          onClose={() => {
            setShowRemoveStylesModal(null);
          }}
        />
      )}
      <PopupAlertModal
        id="hierarchypage-ars-export-error"
        show={arsErrorText !== null}
        title="ARS export failed"
        message={arsErrorText ?? ""}
        preformatted
        onClose={() => {
          setArsErrorText(null);
        }}
      />
    </>
  );
};

HierarchyTopBar.displayName = "HierarchyTopBar";
